import useSWR from 'swr';

export interface WebhookSubscription {
  id: string;
  name: string;
  target_url: string;
  target_type: 'http' | 'slack';
  events: string[];
  active: boolean;
  has_secret: boolean;
  created_by: string;
  created_at: string;
  last_fired_at?: string;
  last_status?: number;
  last_error: string;
}

export interface WebhookDelivery {
  id: number;
  subscription_id: string;
  event: string;
  target_url: string;
  status: 'pending' | 'sent' | 'failed';
  attempts: number;
  response_code?: number;
  last_error: string;
  next_attempt_at: string;
  created_at: string;
  sent_at?: string;
}

const fetcher = (url: string) =>
  fetch(url, { credentials: 'include' }).then((r) => r.json());

export function useWebhooks() {
  return useSWR<WebhookSubscription[]>('/api/admin/webhooks', fetcher);
}

export function useWebhookDeliveries(limit = 50) {
  return useSWR<WebhookDelivery[]>(`/api/admin/webhooks/deliveries?limit=${limit}`, fetcher, {
    refreshInterval: 15_000,
  });
}

export async function createWebhook(input: {
  name: string;
  target_url: string;
  target_type: 'http' | 'slack';
  secret?: string;
  events: string[];
  active: boolean;
}): Promise<void> {
  const res = await fetch('/api/admin/webhooks', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(await res.text());
}

export async function deleteWebhook(id: string): Promise<void> {
  const res = await fetch(`/api/admin/webhooks/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) throw new Error(`delete failed: ${res.status}`);
}

export async function updateWebhook(id: string, patch: Partial<WebhookSubscription>): Promise<void> {
  const res = await fetch(`/api/admin/webhooks/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(patch),
  });
  if (!res.ok && res.status !== 204) throw new Error(`update failed: ${res.status}`);
}

// Webhook event catalogue — populated by GET /api/admin/webhooks/events
// and consumed by the subscription picker so the UI shows
// "Security block created" rather than the raw `block.created` key.

export interface EventDescriptor {
  key: string;
  category: string;
  label: string;
  description: string;
}

export interface EventCategory {
  category: string;
  events: EventDescriptor[];
}

interface EventCatalogueResponse {
  categories: EventCategory[];
}

export function useEventCatalogue() {
  return useSWR<EventCatalogueResponse>('/api/admin/webhooks/events', fetcher, {
    // Catalogue is static within a release. Cache aggressively.
    revalidateOnFocus: false,
    dedupingInterval: 60_000,
  });
}
